@if (readOnly) {
  <clr-alert [clrAlertType]="'info'" [clrAlertClosable]="false">
    <clr-alert-item>
      <span class="alert-text">This group is generated by DC Netscope and updates by itself. Duplicate it to tailor it.</span>
      <div class="alert-actions">
        <clr-dropdown>
          <button type="button" class="dropdown-toggle" clrDropdownTrigger [disabled]="userFiles.length == 0">
            Duplicate into my files
            <cds-icon shape="angle" direction="down"></cds-icon>
          </button>
          <clr-dropdown-menu *clrIfOpen clrPosition="bottom-right">
            @for (file of userFiles; track file) {
              <button type="button" clrDropdownItem (click)="askDuplicate(file)">{{ file }}</button>
            }
          </clr-dropdown-menu>
        </clr-dropdown>
      </div>
    </clr-alert-item>
  </clr-alert>
}

<div class="group-heading">
  <div class="group-identity">
    <div class="clr-form-control">
      <label for="apps-group-name" class="clr-control-label">Name</label>
      <div class="clr-control-container" [class.clr-error]="nameError != undefined">
        <div class="clr-input-wrapper">
          <input id="apps-group-name" class="clr-input name-input" [ngModel]="nameDraft" (ngModelChange)="onNameInput($event)"
                 [readOnly]="readOnly" [attr.aria-invalid]="nameError != undefined" autocomplete="off">
          <cds-icon class="clr-validate-icon" shape="exclamation-circle" status="danger" aria-hidden="true"></cds-icon>
        </div>
        @if (nameError) {
          <span class="clr-subtext" role="alert">{{ nameError }}</span>
        }
      </div>
    </div>
    <div class="clr-form-control">
      <label for="apps-group-description" class="clr-control-label">Description</label>
      <div class="clr-control-container">
        <div class="clr-input-wrapper">
          <input id="apps-group-description" class="clr-input description-input" [ngModel]="group.description"
                 (ngModelChange)="onDescriptionInput($event)" [readOnly]="readOnly"
                 [placeholder]="readOnly ? '' : 'Add a short description'" autocomplete="off">
        </div>
      </div>
    </div>

    <div class="group-facts">
      @if (parents.length > 0) {
        <span class="fact-label">Included in</span>
        @for (parent of parents; track parent.file + '/' + parent.name) {
          <button type="button" class="label label-blue clickable" (click)="openGroup.emit(parent)">
            @if (parent.path.length > 0) {
              {{ parent.path.join(' › ') }} ›
            }
            {{ parent.name }}
            @if (parent.file != editing.file) {
              <span class="badge">{{ parent.file }}</span>
            }
          </button>
        }
      }
      <span class="label" [class.label-success]="descriptionsCount > 0">{{ usedLabel }}</span>
    </div>
  </div>

  <div class="group-actions">
    <button type="button" class="btn btn-sm btn-outline" (click)="showOnMap.emit(group)">
      <cds-icon shape="eye"></cds-icon> Show on the map
    </button>
    <button type="button" class="btn btn-sm btn-outline" (click)="seeFlows.emit(group)">
      <cds-icon shape="two-way-arrows"></cds-icon> See its flows
    </button>
    @if (!readOnly) {
      <button type="button" class="btn btn-sm btn-danger-outline" (click)="confirmDelete = true">
        <cds-icon shape="trash"></cds-icon> Delete
      </button>
    }
  </div>
</div>

<div class="card total-card">
  <div class="card-block">
    <div class="total-row">
      <p class="total"><span class="total-count">{{ members.length }}</span> members right now</p>
      <p class="total-explain">{{ totalExplain }}</p>
    </div>
    <div class="sources-bar large" aria-hidden="true">
      <span class="picked" [style.width]="percentOf('picked')"></span>
      <span class="rule" [style.width]="percentOf('rule')"></span>
      <span class="included" [style.width]="percentOf('included')"></span>
    </div>
  </div>
</div>

<div class="editor-columns">
  <div class="sources">
    <h4 class="column-title">Who belongs to this group</h4>

    <!-- Picked resources -->
    <section class="card source-card picked" aria-labelledby="sec-picked" id="apps-group-editor-picked">
      <div class="card-header">
        <cds-icon shape="check" class="source-icon" aria-hidden="true"></cds-icon>
        <span id="sec-picked">Specific resources</span>
        <span class="badge">{{ picked.length }}</span>
      </div>
      <div class="card-block">
        <p class="card-text source-help">A fixed list: VMs, hosts, networks or IP addresses you pick one by one.</p>
        <div class="chips">
          @for (resource of picked; track resource.uuid) {
            <span class="label">
              <span class="badge">{{ typeLabel(resource.type) }}</span>
              {{ resource.name }}
              @if (!readOnly) {
                <button type="button" class="label-remove" [attr.aria-label]="'Remove ' + resource.name" (click)="removePicked(resource.uuid)">
                  <cds-icon shape="times" size="sm"></cds-icon>
                </button>
              }
            </span>
          } @empty {
            <span class="none">None picked.</span>
          }
        </div>
      </div>
      @if (!readOnly) {
        <div class="card-footer source-footer">
          <clr-combobox-container>
            <label for="pick-search">Add a resource</label>
            <clr-combobox id="pick-search" name="pick" [ngModel]="pickSelection" (ngModelChange)="onPickSelected($event)"
                          (clrInputChange)="onPickQuery($event)"
                          placeholder="A VM, host, network or IP… try a name or “10.0”">
              <ng-container *clrOptionSelected="let selected">{{ selected?.name }}</ng-container>
              <clr-options>
                <clr-option *clrOptionItems="let suggestion of suggestions; field: 'searchText'" [clrValue]="suggestion">
                  <span class="badge">{{ typeLabel(suggestion.type) }}</span>
                  {{ suggestion.name }}
                  @if (suggestion.address && suggestion.address != suggestion.name) {
                    <span class="option-detail">{{ suggestion.address }}</span>
                  }
                </clr-option>
              </clr-options>
            </clr-combobox>
            @if (pickQuery.trim() !== '' && suggestions.length == 0) {
              <clr-control-helper>Nothing in the inventory matches “{{ pickQuery }}”. To follow names that do not exist yet, use a name rule below.</clr-control-helper>
            }
          </clr-combobox-container>
        </div>
      }
    </section>

    <!-- Name rules -->
    <section class="card source-card rule" aria-labelledby="sec-rules" id="apps-group-editor-rules">
      <div class="card-header">
        <cds-icon shape="filter" class="source-icon" aria-hidden="true"></cds-icon>
        <span id="sec-rules">Name rules</span>
        <span class="badge">{{ ruleMatchesLabel }}</span>
      </div>
      <div class="card-block">
        <p class="card-text source-help">Follows the inventory: a new VM or host named like this joins the group by itself. Use <code>*</code> for any text.</p>
        <div class="rule-row">
          <span class="rule-label">Names like</span>
          @for (pattern of patterns('include'); track pattern) {
            <span class="label label-success">
              <code>{{ pattern }}</code>
              <span class="badge">{{ hitsLabel(pattern) }}</span>
              @if (!readOnly) {
                <button type="button" class="label-remove" [attr.aria-label]="'Remove pattern ' + pattern" (click)="removePattern('include', pattern)">
                  <cds-icon shape="times" size="sm"></cds-icon>
                </button>
              }
            </span>
          } @empty {
            <span class="none">no rule</span>
          }
        </div>
        <div class="rule-row">
          <span class="rule-label">except</span>
          @for (pattern of patterns('exclude'); track pattern) {
            <span class="label label-danger">
              <code>{{ pattern }}</code>
              <span class="badge">{{ hitsLabel(pattern) }}</span>
              @if (!readOnly) {
                <button type="button" class="label-remove" [attr.aria-label]="'Remove exception ' + pattern" (click)="removePattern('exclude', pattern)">
                  <cds-icon shape="times" size="sm"></cds-icon>
                </button>
              }
            </span>
          } @empty {
            <span class="none">nothing</span>
          }
        </div>
        @if (ruleSample.length > 0) {
          <p class="rule-sample">
            @for (name of ruleSample; track name) {
              <code>{{ name }}</code>
            }
            @if (ruleSampleMore) {
              <span class="none">{{ ruleSampleMore }}</span>
            }
          </p>
        }
        @if (patternsError) {
          <clr-alert [clrAlertType]="'danger'" [clrAlertClosable]="false" [clrAlertSizeSmall]="true">
            <clr-alert-item><span class="alert-text">{{ patternsError }}</span></clr-alert-item>
          </clr-alert>
        }
      </div>
      @if (!readOnly) {
        <div class="card-footer source-footer">
          <div class="rule-form">
            <div class="btn-group btn-outline-primary btn-sm" role="radiogroup" aria-label="Kind of rule">
              <div class="radio btn">
                <input type="radio" name="apps-group-pattern-kind" id="pattern-kind-include" value="include"
                       [checked]="patternKind == 'include'" (change)="setPatternKind('include')">
                <label for="pattern-kind-include">Include</label>
              </div>
              <div class="radio btn">
                <input type="radio" name="apps-group-pattern-kind" id="pattern-kind-exclude" value="exclude"
                       [checked]="patternKind == 'exclude'" (change)="setPatternKind('exclude')">
                <label for="pattern-kind-exclude">Except</label>
              </div>
            </div>
            <div class="clr-input-wrapper pattern-wrapper">
              <label for="pattern-draft" class="clr-sr-only">Name pattern</label>
              <input id="pattern-draft" class="clr-input pattern-input" [ngModel]="patternDraft" (ngModelChange)="onPatternDraft($event)"
                     (keydown.enter)="addPattern()" placeholder="e.g. web-prod-*" autocomplete="off">
            </div>
            <button type="button" class="btn btn-sm btn-primary" (click)="addPattern()" [disabled]="patternDraft.trim() === ''">
              Add rule
            </button>
          </div>
          @if (draftPreview) {
            <p class="clr-subtext draft-preview">{{ draftPreview }}</p>
          }
        </div>
      }
    </section>

    <!-- Other groups -->
    <section class="card source-card included" aria-labelledby="sec-groups" id="apps-group-editor-groups">
      <div class="card-header">
        <cds-icon shape="layers" class="source-icon" aria-hidden="true"></cds-icon>
        <span id="sec-groups">Other groups</span>
        <span class="badge">{{ countOf('included') }} members brought in</span>
      </div>
      <div class="card-block">
        <p class="card-text source-help">Build a larger group from smaller ones, from this file or another. They stay linked: change a group, every group including it follows.</p>
        <div class="chips">
          @for (included of included; track included.ref.file + '/' + included.ref.name) {
            <span class="label" [class.label-warning]="!included.missing" [class.label-danger]="included.missing">
              <button type="button" class="label-open" (click)="openGroup.emit(included.ref)" [disabled]="included.missing">
                {{ included.ref.name }}
              </button>
              <span class="badge">{{ includedWhere(included.ref, included.missing) }}</span>
              @if (!readOnly) {
                <button type="button" class="label-remove" [attr.aria-label]="'Stop including ' + included.ref.name" (click)="removeInclusion(included.ref)">
                  <cds-icon shape="times" size="sm"></cds-icon>
                </button>
              }
            </span>
          } @empty {
            <span class="none">No group included.</span>
          }
        </div>
      </div>
      @if (!readOnly) {
        <div class="card-footer source-footer">
          <clr-combobox-container>
            <label for="group-search">Include a group</label>
            <clr-combobox id="group-search" name="includeGroup" [ngModel]="groupSelection"
                          (ngModelChange)="onGroupSelected($event)" placeholder="Filter by group or file name">
              <ng-container *clrOptionSelected="let selected">{{ selected?.ref.name }}</ng-container>
              <clr-options>
                <clr-option *clrOptionItems="let candidate of candidates; field: 'searchText'" [clrValue]="candidate">
                  <strong>{{ candidate.ref.name }}</strong>
                  <span class="option-detail">{{ candidate.fileLabel }} · {{ candidate.countLabel }}</span>
                </clr-option>
              </clr-options>
            </clr-combobox>
            @if (blockedCandidates.length > 0) {
              <clr-control-helper>
                <cds-icon shape="sync" size="sm"></cds-icon>
                Not offered, they already include {{ group.name }}: {{ blockedCandidates.join(', ') }}
              </clr-control-helper>
            }
          </clr-combobox-container>
        </div>
      }
    </section>
  </div>

  <section class="card members-card" aria-labelledby="members-title">
    <div class="card-header">
      <span id="members-title">Resulting members</span>
    </div>
    <div class="card-block">
      <p class="card-text source-help">What the map, filters and flows descriptions will see, and why each one is here.</p>
      <div class="btn-group btn-outline-primary btn-sm members-filters" role="radiogroup" aria-label="Filter members">
        @for (filter of membersFilters; track filter.key) {
          <div class="radio btn">
            <input type="radio" name="apps-group-members-filter" [id]="'members-filter-' + filter.key" [value]="filter.key"
                   [checked]="membersFilter == filter.key" (change)="membersFilter = filter.key">
            <label [for]="'members-filter-' + filter.key">{{ filter.label }}</label>
          </div>
        }
      </div>

      <clr-datagrid class="datagrid-compact members-grid">
        <clr-dg-column [clrDgField]="'typeLabel'" [style.width.px]="90">Type</clr-dg-column>
        <clr-dg-column [clrDgField]="'name'" [clrDgSortOrder]="1">Name</clr-dg-column>
        <clr-dg-column [clrDgField]="'why'" [style.width.px]="130">Why</clr-dg-column>

        <clr-dg-row *clrDgItems="let member of filteredMembers; trackBy: trackMember" [clrDgItem]="member">
          <clr-dg-cell>{{ member.typeLabel }}</clr-dg-cell>
          <clr-dg-cell class="member-name" [title]="member.name">{{ member.name }}</clr-dg-cell>
          <clr-dg-cell><span [class]="'dot ' + member.source"></span>{{ member.why }}</clr-dg-cell>
        </clr-dg-row>

        <clr-dg-placeholder>Nobody here yet. Pick resources, add a name rule or include a group.</clr-dg-placeholder>

        <clr-dg-footer>
          <clr-dg-pagination #membersPagination [clrDgPageSize]="15">
            @if (membersPagination.totalItems > 0) {
              {{ membersPagination.firstItem + 1 }} - {{ membersPagination.lastItem + 1 }} of {{ membersPagination.totalItems }} members
            }
          </clr-dg-pagination>
        </clr-dg-footer>
      </clr-datagrid>
    </div>
  </section>
</div>

<clr-modal [(clrModalOpen)]="confirmDelete" [clrModalSize]="'md'">
  <h3 class="modal-title">Delete {{ group.name }}?</h3>
  <div class="modal-body">
    <p>The groups of this file including it stop including it.</p>
    @if (externalParentsCount > 0) {
      <p>
        {{ externalParentsCount }} {{ externalParentsCount == 1 ? 'group of another file includes' : 'groups of other files include' }}
        it and will point to a missing group.
      </p>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline" (click)="confirmDelete = false">Keep it</button>
    <button type="button" class="btn btn-danger" (click)="deleteGroup()">Delete the group</button>
  </div>
</clr-modal>
